import { consola } from 'consola'
import { authLogoutIsClient } from './authLogoutIsClient'
import { useTabsSync, TabSyncEventType } from '@shared/lib'
import { useCookie } from '#imports'
import { navigateTo } from '#app'

// Logger для сервиса разлогинивания
const logger = consola.withTag('auth-logout-service')

/**
 * Причины разлогинивания для детального логирования
 */
export enum LogoutReason {
  USER_INITIATED = 'USER_INITIATED', // Пользователь нажал "Выйти"
  TOKEN_EXPIRED = 'TOKEN_EXPIRED', // Access token истек
  REFRESH_TOKEN_EXPIRED = 'REFRESH_TOKEN_EXPIRED', // Refresh token истек
  REFRESH_TOKEN_INVALID = 'REFRESH_TOKEN_INVALID', // Refresh token недействителен
  SESSION_INVALID = 'SESSION_INVALID', // Сессия недействительна
  MAX_RETRY_EXCEEDED = 'MAX_RETRY_EXCEEDED', // Превышен лимит попыток обновления
  API_ERROR_401 = 'API_ERROR_401', // Ошибка 401 от API
  API_ERROR_403 = 'API_ERROR_403', // Ошибка 403 от API
  PROFILE_DELETED = 'PROFILE_DELETED', // Профиль удален
  ADMIN_LOGOUT = 'ADMIN_LOGOUT', // Выход из админ-панели
  SECURITY_ISSUE = 'SECURITY_ISSUE', // Проблемы безопасности
}

/**
 * Тип пользователя для разлогинивания
 */
export enum UserType {
  REGULAR = 'REGULAR', // Обычный пользователь (V2 система)
  ADMIN = 'ADMIN', // Администратор (TODO: Мигрировать на V2 когда бэкенд будет готов)
}

/**
 * Опции для разлогинивания
 */
export interface LogoutOptions {
  reason: LogoutReason
  userType: UserType
  redirectTo?: string
  clearStorage?: boolean
  silent?: boolean // Тихое разлогинивание без уведомлений
  metadata?: Record<string, any> // Дополнительные данные для отладки
}

/**
 * Singleton паттерн для предотвращения множественных разлогиниваний
 */
let isLoggingOut = false
let logoutPromise: Promise<void> | null = null

/**
 * Централизованный сервис для управления разлогиниванием
 * 
 * ✅ Единственный источник истины для всех операций разлогинивания
 * ✅ Детальное логирование для отладки
 * ✅ Защита от race conditions
 * ✅ Основная поддержка V2 системы
 * 
 * TODO: Методы V1 (clearV1Tokens) оставлены для совместимости с админами
 * Когда бэкенд админской панели будет готов к V2, мигрировать на единую V2 систему
 * 
 * @example
 * // Явное разлогинивание пользователя
 * await AuthLogoutService.logout({
 *   reason: LogoutReason.USER_INITIATED,
 *   userType: UserType.REGULAR
 * })
 * 
 * @example
 * // Тихое разлогинивание при истечении токена
 * await AuthLogoutService.logout({
 *   reason: LogoutReason.REFRESH_TOKEN_EXPIRED,
 *   userType: UserType.REGULAR,
 *   silent: true,
 *   metadata: { lastTokenRefresh: Date.now() }
 * })
 */
export class AuthLogoutService {
  /**
   * Основной метод разлогинивания
   * 
   * @param options - Опции разлогинивания
   * @returns Promise<void>
   */
  static async logout(options: LogoutOptions): Promise<void> {
    // Защита от множественных одновременных разлогиниваний
    if (isLoggingOut && logoutPromise) {
      logger.warn('🔒 Разлогинивание уже выполняется, ожидаем завершения')
      return logoutPromise
    }

    isLoggingOut = true
    
    logoutPromise = this.performLogout(options)
    
    try {
      await logoutPromise
    } finally {
      isLoggingOut = false
      logoutPromise = null
    }
  }

  /**
   * Выполняет процесс разлогинивания
   */
  private static async performLogout(options: LogoutOptions): Promise<void> {
    const {
      reason,
      userType,
      redirectTo = '/',
      clearStorage = true,
      silent = false,
      metadata = {},
    } = options

    // 📊 ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ для отладки
    let currentUrl = 'SSR'
    let userAgent = 'SSR'
    if (authLogoutIsClient()) {
      currentUrl = window.location.href
      userAgent = navigator.userAgent
    }

    const logData = {
      timestamp: new Date().toISOString(),
      reason,
      userType,
      redirectTo,
      clearStorage,
      silent,
      metadata,
      currentUrl,
      userAgent,
    }

    if (silent) {
      logger.info('🔕 Тихое разлогинивание:', logData)
    } else {
      logger.warn('🚪 РАЗЛОГИНИВАНИЕ:', logData)
    }

    // ✅ Уведомляем другие вкладки о разлогине
    if (authLogoutIsClient()) {
      try {
        const tabsSync = useTabsSync()
        tabsSync.broadcast(TabSyncEventType.USER_LOGGED_OUT, {
          reason,
          timestamp: Date.now(),
        })
        logger.info('📤 Уведомление о разлогине отправлено в другие вкладки')
      } catch (error) {
        logger.warn('Не удалось отправить уведомление о разлогине:', error)
      }
    }
    
    // Трекаем событие разлогинивания для отладки
    if (authLogoutIsClient()) {
      try {
        const { useLogoutEventTracker } = await import('../model/useLogoutEventTracker')
        const tracker = useLogoutEventTracker()
        tracker.trackLogoutEvent({
          reason: reason.toString(),
          source: metadata.source || 'unknown',
          metadata: logData,
        })
      } catch (error) {
        logger.warn('Не удалось записать событие в трекер:', error)
      }
    }

    try {
      // 1. Очищаем токены и сессии
      if (clearStorage) {
        await this.clearAuthData(userType)
      }

      // 2. Очищаем данные пользователя
      await this.clearUserData()

      // 3. Очищаем кэш
      await this.clearCache()

      // 4. Уведомляем другие вкладки о разлогинивании
      this.notifyOtherTabs(reason)

      // 5. Редирект
      await this.performRedirect(redirectTo, userType)

      logger.success('✅ Разлогинивание успешно завершено')

    } catch (error) {
      logger.error('❌ Ошибка при разлогинивании:', error)
      
      // В любом случае пытаемся очистить данные и сделать редирект
      try {
        await this.clearAuthData(userType)
        await this.clearUserData()
        await this.performRedirect(redirectTo, userType)
      } catch (fallbackError) {
        logger.error('❌ Критическая ошибка при fallback разлогинивании:', fallbackError)
      }
    }
  }

  /**
   * Очищает токены и сессии
   */
  private static async clearAuthData(userType: UserType): Promise<void> {
    logger.debug('🧹 Очистка токенов и сессий...')

    if (userType === UserType.REGULAR) {
      // V2 система (обычные пользователи)
      await this.clearV2Tokens()
    } else {
      // TODO: V1 система (админы) - мигрировать на V2 когда бэкенд будет готов
      logger.warn('⚠️ Используем V1 логику для админов (legacy)')
      await this.clearV1Tokens()
    }

    logger.success('✅ Токены и сессии очищены')
  }

  /**
   * Очищает токены V2 системы (обычные пользователи)
   * ✅ ОБНОВЛЕНО: Очищаем localStorage вместо cookies
   */
  private static async clearV2Tokens(): Promise<void> {
    // Очищаем localStorage (теперь храним токены здесь)
    if (authLogoutIsClient()) {
      localStorage.removeItem('authAccessToken')
      localStorage.removeItem('authRefreshToken') // ✅ КРИТИЧНО: Очищаем refresh token
      localStorage.removeItem('authSessionId')
      localStorage.removeItem('authTokenExpiry')
      localStorage.removeItem('refresh_token_invalid')
      localStorage.removeItem('userId')
    }

    // Очищаем SecureStorage на нативных платформах
    if (authLogoutIsClient()) {
      try {
        const { Capacitor } = await import('@capacitor/core')
        if (Capacitor.isNativePlatform()) {
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          await SecureStoragePlugin.remove({ key: 'authAccessToken' })
          await SecureStoragePlugin.remove({ key: 'authRefreshToken' }) // ✅ КРИТИЧНО: Очищаем refresh token
          await SecureStoragePlugin.remove({ key: 'authSessionId' })
          logger.success('✅ V2 токены очищены из SecureStorage')
        }
      } catch (error) {
        logger.warn('⚠️ Ошибка очистки V2 токенов из SecureStorage:', error)
      }
    }

    // Очищаем настройки push-уведомлений
    if (authLogoutIsClient()) {
      try {
        const { clearPushNotificationSettingsStorage } = await import('@shared/lib')
        await clearPushNotificationSettingsStorage()
        logger.success('✅ Настройки push-уведомлений очищены')
      } catch (error) {
        logger.warn('⚠️ Ошибка очистки настроек push-уведомлений:', error)
      }
    }

    // ✅ Очищаем номер телефона заведения
    if (authLogoutIsClient()) {
      try {
        const { useInstitutionPhoneStorage } = await import('~/entities/institution/@x/auth')
        const phoneStorage = useInstitutionPhoneStorage()
        await phoneStorage.removeInstitutionPhone()
        logger.success('✅ Номер телефона заведения очищен')
      } catch (error) {
        logger.warn('⚠️ Ошибка очистки номера телефона заведения:', error)
      }
    }
  }

  /**
   * Очищает токены V1 системы (админы)
   * 
   * TODO: LEGACY - Удалить когда админы мигрируют на V2
   * Этот метод оставлен для совместимости со старой админской авторизацией
   */
  private static async clearV1Tokens(): Promise<void> {
    logger.warn('⚠️ LEGACY: Очистка V1 токенов (админы)')
    
    // Очищаем cookies
    const authAdminAccessToken = useCookie<string | null>('authAdminAccessToken')
    const authAdminRefreshToken = useCookie<string | null>('authAdminRefreshToken')
    authAdminAccessToken.value = null
    authAdminRefreshToken.value = null

    // Очищаем SecureStorage на нативных платформах
    if (authLogoutIsClient()) {
      try {
        const { Capacitor } = await import('@capacitor/core')
        if (Capacitor.isNativePlatform()) {
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          await SecureStoragePlugin.remove({ key: 'authAdminAccessToken' })
          await SecureStoragePlugin.remove({ key: 'authAdminRefreshToken' })
          logger.success('✅ V1 токены очищены из SecureStorage')
        }
      } catch (error) {
        logger.warn('⚠️ Ошибка очистки V1 токенов из SecureStorage:', error)
      }
    }
  }

  /**
   * Очищает данные пользователя из store
   */
  private static async clearUserData(): Promise<void> {
    logger.debug('🧹 Очистка данных пользователя...')

    try {
      const { useUserProfileStore } = await import('~/entities/user/@x/auth')
      const userProfileStore = useUserProfileStore()
      userProfileStore.clearUser()
      logger.success('✅ Данные пользователя очищены')
    } catch (error) {
      logger.warn('⚠️ Ошибка очистки данных пользователя:', error)
    }
  }

  /**
   * Очищает кэш приложения
   */
  private static async clearCache(): Promise<void> {
    logger.debug('🧹 Очистка кэша...')

    try {
      const { offlineManager } = await import('@shared/lib/networking')
      offlineManager.clearCache()
      logger.success('✅ Кэш очищен')
    } catch (error) {
      logger.warn('⚠️ Ошибка очистки кэша:', error)
    }
  }

  /**
   * Уведомляет другие вкладки о разлогинивании
   */
  private static notifyOtherTabs(reason: LogoutReason): void {
    if (!authLogoutIsClient()) {
      return
    }

    try {
      localStorage.setItem('logout_event', JSON.stringify({
        reason,
        timestamp: Date.now(),
      }))
      
      // Удаляем сразу, чтобы событие сработало при следующей установке
      localStorage.removeItem('logout_event')
      
      logger.debug('📢 Другие вкладки уведомлены о разлогинивании')
    } catch (error) {
      logger.warn('⚠️ Ошибка уведомления других вкладок:', error)
    }
  }

  /**
   * Выполняет редирект после разлогинивания
   */
  private static async performRedirect(redirectTo: string, userType: UserType): Promise<void> {
    if (!authLogoutIsClient()) {
      return
    }

    logger.debug(`🔄 Редирект на: ${redirectTo}`)

    // Для админов редиректим на страницу логина админки
    const finalRedirect = userType === UserType.ADMIN && redirectTo === '/' 
      ? '/administration/home' 
      : redirectTo

    try {
      // Используем window.location для жесткого редиректа (очистка состояния)
      window.location.href = finalRedirect
    } catch (error) {
      // Fallback на navigateTo
      logger.warn('⚠️ Ошибка window.location, используем navigateTo:', error)
      await navigateTo(finalRedirect)
    }
  }

  /**
   * Проверяет, выполняется ли сейчас разлогинивание
   */
  static isLoggingOut(): boolean {
    return isLoggingOut
  }

  /**
   * Логирует попытку разлогинивания (для отладки)
   */
  static logLogoutAttempt(source: string, reason: string, metadata?: Record<string, any>): void {
    logger.info('🔍 Попытка разлогинивания:', {
      source,
      reason,
      metadata,
      timestamp: new Date().toISOString(),
      isLoggingOut,
    })
  }
}

/**
 * Composable для удобного использования в Vue компонентах
 */
export const useAuthLogout = () => {
  /**
   * Разлогинить обычного пользователя
   */
  const logoutUser = async (reason: LogoutReason = LogoutReason.USER_INITIATED, metadata?: Record<string, any>) => {
    await AuthLogoutService.logout({
      reason,
      userType: UserType.REGULAR,
      metadata,
    })
  }

  /**
   * Разлогинить администратора
   * 
   * TODO: LEGACY - Использует V1 систему, мигрировать на V2 когда бэкенд будет готов
   */
  const logoutAdmin = async (reason: LogoutReason = LogoutReason.ADMIN_LOGOUT, metadata?: Record<string, any>) => {
    logger.warn('⚠️ LEGACY: Разлогинивание админа через V1')
    await AuthLogoutService.logout({
      reason,
      userType: UserType.ADMIN,
      redirectTo: '/administration/home',
      metadata,
    })
  }

  /**
   * Тихое разлогинивание (при истечении токенов)
   */
  const silentLogout = async (reason: LogoutReason, userType: UserType, metadata?: Record<string, any>) => {
    await AuthLogoutService.logout({
      reason,
      userType,
      silent: true,
      metadata,
    })
  }

  return {
    logoutUser,
    logoutAdmin,
    silentLogout,
    isLoggingOut: () => AuthLogoutService.isLoggingOut(),
  }
}

